import { apiGet, apiPost } from "./api";

export interface CurrentUser {
    id: number;
    username: string;
    email: string;
    profilePictureUrl?: string | null;
}

export function fetchCurrentUser(): Promise<CurrentUser> {
    return apiGet<CurrentUser>("/v1/users/me");
}

export interface UserSearchResult {
    id: number;
    username: string;
    profilePictureUrl?: string | null;
    playlistCount: number;
}

/**
 * People search by username. Resolves to an empty list for a blank query rather than
 * asking the server to match everything.
 */
export function searchUsers(query: string): Promise<UserSearchResult[]> {
    const trimmed = query.trim();
    if (!trimmed) {
        return Promise.resolve([]);
    }
    return apiGet<UserSearchResult[]>(`/v1/users/search?q=${encodeURIComponent(trimmed)}`);
}

/**
 * Starts a password reset. Always resolves the same way whether or not the address has
 * an account — the server deliberately gives nothing away, so neither does this.
 */
export function requestPasswordReset(email: string): Promise<{ message: string }> {
    return apiPost<{ message: string }>("/v1/users/forgotPassword", { email });
}

/** Completes a reset. The response also establishes the session cookie. */
export function resetPassword(token: string, newPassword: string): Promise<{ token: string }> {
    return apiPost<{ token: string }>("/v1/users/resetPassword", { token, newPassword });
}

/** Confirms an email address from the link in the verification email. */
export function verifyEmail(token: string): Promise<{ message: string }> {
    return apiGet<{ message: string }>(`/v1/users/verifyEmail?token=${encodeURIComponent(token)}`);
}

/** Issues a fresh verification email for the signed-in account. */
export function resendVerification(): Promise<{ message: string }> {
    return apiPost<{ message: string }>("/v1/users/resendVerification");
}
